/**
 * Tab-style blueprint mode switcher for the chart card
 * (DNA, Da Yun, Liu Nian this/next year, Liu Month).
 */

import React, { useMemo } from "react";
import { Calendar, Clock, Sparkles } from "lucide-react";
import {
  chartBlueprintSwitcherClass,
  chartBlueprintTabActiveClass,
  chartBlueprintTabIconClass,
  chartBlueprintTabInactiveClass,
} from "../../../styles/chartUi";

export type ChartBlueprintMode =
  | "dna"
  | "dayun"
  | "liunian"
  | "liunianNext"
  | "liumonth";

type BlueprintTabConfig = {
  key: ChartBlueprintMode;
  /** Primary label shown on the first line (e.g. "Liu Nian"). */
  title: string;
  /** Optional subtitle under the title (e.g. "(2026)"). */
  description?: string;
  /** Compact mobile label when description is omitted or truncated. */
  shortLabel: string;
  icon: React.ReactNode;
};

type ChartBlueprintSwitcherProps = {
  value: ChartBlueprintMode;
  onChange: (mode: ChartBlueprintMode) => void;
  className?: string;
};

/**
 * True when the blueprint tab is either this-year or next-year Liu Nian.
 */
export function isLiuNianBlueprintMode(mode: ChartBlueprintMode): boolean {
  return mode === "liunian" || mode === "liunianNext";
}

/**
 * Gregorian year for a Liu Nian blueprint tab (rolling this / next year).
 * Returns null for non–Liu Nian modes.
 */
export function getLiuNianYearForBlueprintMode(
  mode: ChartBlueprintMode,
  now: Date = new Date()
): number | null {
  if (mode === "liunian") {
    return now.getFullYear();
  }
  if (mode === "liunianNext") {
    return now.getFullYear() + 1;
  }
  return null;
}

/**
 * Calendar with a small sparkle — matches the annual-energy tab reference art.
 */
const AnnualEnergyIcon: React.FC<{ className?: string }> = ({ className }) => (
  <span className={`relative inline-flex shrink-0 ${className ?? ""}`} aria-hidden="true">
    <Calendar className="h-full w-full" />
    <Sparkles className="absolute -right-0.5 -top-0.5 h-2 w-2 sm:h-2.5 sm:w-2.5 text-accent-gold dark:text-accent-goldDark" />
  </span>
);

/**
 * Branded tab strip for switching chart blueprint overlays.
 */
const ChartBlueprintSwitcher: React.FC<ChartBlueprintSwitcherProps> = ({
  value,
  onChange,
  className = "",
}) => {
  const tabs = useMemo((): BlueprintTabConfig[] => {
    const thisYear = new Date().getFullYear();
    const nextYear = thisYear + 1;
    const annualIconClass = `${chartBlueprintTabIconClass} h-3.5 w-3.5 sm:h-4 sm:w-4`;

    return [
      {
        key: "dna",
        title: "DNA Chart",
        shortLabel: "DNA",
        icon: <Sparkles className={chartBlueprintTabIconClass} aria-hidden="true" />,
      },
      {
        key: "dayun",
        title: "Da Yun",
        description: "(10 Year)",
        shortLabel: "10 Year",
        icon: <Clock className={chartBlueprintTabIconClass} aria-hidden="true" />,
      },
      {
        key: "liunian",
        title: "Liu Nian",
        description: `(${thisYear})`,
        shortLabel: String(thisYear),
        icon: <AnnualEnergyIcon className={annualIconClass} />,
      },
      {
        key: "liunianNext",
        title: "Liu Nian",
        description: `(${nextYear})`,
        shortLabel: String(nextYear),
        icon: <AnnualEnergyIcon className={annualIconClass} />,
      },
      {
        key: "liumonth",
        title: "Liu Month",
        description: "(Monthly)",
        shortLabel: "Month",
        icon: <Calendar className={chartBlueprintTabIconClass} aria-hidden="true" />,
      },
    ];
  }, []);

  return (
    <div
      className={`${chartBlueprintSwitcherClass} ${className}`.trim()}
      role="tablist"
      aria-label="Chart blueprint mode"
    >
      {tabs.map((tab) => {
        const active = value === tab.key;
        const ariaLabel = tab.description
          ? `${tab.title} ${tab.description}`
          : tab.title;

        return (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={active}
            aria-label={ariaLabel}
            onClick={() => onChange(tab.key)}
            className={active ? chartBlueprintTabActiveClass : chartBlueprintTabInactiveClass}
          >
            {tab.icon}
            <span className="flex min-w-0 flex-col items-start justify-center leading-tight">
              <span className="truncate text-left sm:hidden">{tab.shortLabel}</span>
              <span className="hidden truncate text-left sm:block">{tab.title}</span>
              {tab.description ? (
                <span
                  className={`mt-0.5 hidden truncate text-left text-[9px] font-medium sm:block sm:text-[10px] ${
                    active ? "text-cream/80" : "text-theme-fg-secondary"
                  }`}
                >
                  {tab.description}
                </span>
              ) : null}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default ChartBlueprintSwitcher;
